<script setup>
/**
 * Create or edit a ticket type: its name, its audience, and whether its
 * labelled stages narrate progress back to the customer.
 *
 * Visibility is the whole reason this dialog matters. A ticket does not carry
 * its own audience — its type does — so each mode is stated in plain language
 * next to the control, in the same words as the visibility doc, rather than
 * leaving an admin to work out what "team private" buys them.
 *
 * The team picker is checkboxes, not a combobox: a workspace has a handful of
 * teams, and picking an audience is a thing you want to see all of at once.
 * `teamIds` is sent whole — it states the audience rather than patching it.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

defineProps({
  // `{ id, name }` — every team in the workspace.
  teams: {
    type: Array,
    default: () => [],
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'close']);

const { t } = useI18n();

// Order matters: the most open mode first, so the list reads as a narrowing.
const SHARING_MODES = [
  {
    value: 'workspace',
    labelKey: 'SUPPORT_CASES.SETTINGS.FORM.VISIBILITY_WORKSPACE',
    hintKey: 'SUPPORT_CASES.SETTINGS.FORM.VISIBILITY_WORKSPACE_HINT',
  },
  {
    value: 'selected_teams',
    labelKey: 'SUPPORT_CASES.SETTINGS.FORM.VISIBILITY_SELECTED_TEAMS',
    hintKey: 'SUPPORT_CASES.SETTINGS.FORM.VISIBILITY_SELECTED_TEAMS_HINT',
  },
  {
    value: 'team_private',
    labelKey: 'SUPPORT_CASES.SETTINGS.FORM.VISIBILITY_TEAM_PRIVATE',
    hintKey: 'SUPPORT_CASES.SETTINGS.FORM.VISIBILITY_TEAM_PRIVATE_HINT',
  },
];

const dialogRef = ref(null);
const editing = ref(null);
const name = ref('');
const description = ref('');
const sharing = ref('team_private');
const teamIds = ref([]);
const shareProgress = ref(false);

const isEditing = computed(() => Boolean(editing.value));
const canSubmit = computed(() => Boolean(name.value.trim()));

// `workspace` ignores the join table entirely (visibility.md), so offering the
// picker there would suggest the choice does something.
const showTeamPicker = computed(() => sharing.value !== 'workspace');

const open = (caseType = null) => {
  editing.value = caseType;
  name.value = caseType?.name || '';
  description.value = caseType?.description || '';
  sharing.value = caseType?.sharing || 'team_private';
  teamIds.value = (caseType?.teams || []).map(team => team.id);
  shareProgress.value = Boolean(caseType?.shareProgressWithCustomer);
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const toggleTeam = (teamId, checked) => {
  teamIds.value = checked
    ? [...teamIds.value, teamId]
    : teamIds.value.filter(id => id !== teamId);
};

const onSubmit = () => {
  if (!canSubmit.value) return;
  emit('submit', {
    id: editing.value?.id || null,
    payload: {
      name: name.value.trim(),
      description: description.value.trim() || null,
      sharing: sharing.value,
      shareProgressWithCustomer: shareProgress.value,
      teamIds: teamIds.value,
    },
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="
      isEditing
        ? t('SUPPORT_CASES.SETTINGS.FORM.EDIT_TITLE')
        : t('SUPPORT_CASES.SETTINGS.FORM.CREATE_TITLE')
    "
    :confirm-button-label="
      isEditing
        ? t('SUPPORT_CASES.SETTINGS.FORM.SAVE')
        : t('SUPPORT_CASES.SETTINGS.FORM.CREATE')
    "
    :cancel-button-label="t('SUPPORT_CASES.SETTINGS.FORM.CANCEL')"
    :disable-confirm-button="!canSubmit"
    :is-loading="isSubmitting"
    :close-on-click-outside="false"
    @confirm="onSubmit"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-5" data-testid="ticket-type-dialog">
      <Input
        v-model="name"
        :label="t('SUPPORT_CASES.SETTINGS.FORM.NAME_LABEL')"
        :placeholder="t('SUPPORT_CASES.SETTINGS.FORM.NAME_PLACEHOLDER')"
        data-testid="ticket-type-name"
      />

      <TextArea
        v-model="description"
        :label="t('SUPPORT_CASES.SETTINGS.FORM.DESCRIPTION_LABEL')"
        :placeholder="t('SUPPORT_CASES.SETTINGS.FORM.DESCRIPTION_PLACEHOLDER')"
        :max-length="500"
        data-testid="ticket-type-description"
      />

      <fieldset class="flex flex-col gap-2 p-0 m-0 border-0">
        <legend class="p-0 mb-1 text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.SETTINGS.FORM.VISIBILITY_TITLE') }}
        </legend>

        <label
          v-for="mode in SHARING_MODES"
          :key="mode.value"
          class="flex gap-3 p-3 border rounded-lg cursor-pointer border-n-weak"
          :class="sharing === mode.value ? 'bg-n-alpha-1' : ''"
          :data-testid="`ticket-type-sharing-${mode.value}`"
        >
          <input
            v-model="sharing"
            type="radio"
            name="ticket-type-sharing"
            :value="mode.value"
            class="mt-1 accent-n-brand"
          />
          <span class="flex flex-col gap-0.5">
            <span class="text-sm text-n-slate-12">{{ t(mode.labelKey) }}</span>
            <span class="text-xs text-n-slate-11">{{ t(mode.hintKey) }}</span>
          </span>
        </label>

        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('SUPPORT_CASES.SETTINGS.FORM.VISIBILITY_ALWAYS') }}
        </p>
      </fieldset>

      <fieldset
        v-if="showTeamPicker"
        class="flex flex-col gap-2 p-0 m-0 border-0"
        data-testid="ticket-type-teams"
      >
        <legend class="p-0 mb-1 text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.SETTINGS.FORM.TEAMS_LABEL') }}
        </legend>

        <p v-if="!teams.length" class="mb-0 text-sm text-n-slate-11">
          {{ t('SUPPORT_CASES.SETTINGS.FORM.TEAMS_EMPTY') }}
        </p>

        <div
          v-for="team in teams"
          :key="team.id"
          class="flex items-center gap-2"
        >
          <Checkbox
            :id="`ticket-type-team-${team.id}`"
            :model-value="teamIds.includes(team.id)"
            :data-testid="`ticket-type-team-${team.id}`"
            @update:model-value="checked => toggleTeam(team.id, checked)"
          />
          <label
            :for="`ticket-type-team-${team.id}`"
            class="text-sm cursor-pointer text-n-slate-12"
          >
            {{ team.name }}
          </label>
        </div>
      </fieldset>

      <div class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak">
        <div class="flex items-start gap-3">
          <Switch
            v-model="shareProgress"
            data-testid="ticket-type-share-progress"
          />
          <span class="flex flex-col gap-0.5">
            <span class="text-sm text-n-slate-12">
              {{ t('SUPPORT_CASES.SETTINGS.FORM.PROGRESS_TITLE') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('SUPPORT_CASES.SETTINGS.FORM.PROGRESS_DESCRIPTION') }}
            </span>
          </span>
        </div>
      </div>
    </div>
  </Dialog>
</template>
